/**
 * Ingredient Tabs Updated Modal
 * Handles drawer/bottom-sheet modal with focus trap, accessibility, and content population
 */

(function() {
  'use strict';

  // Wait for DOM to be ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  function init() {
    console.log('Ingredient modal script initialized');

    // Portal modal overlays to <body> to escape .shopify-section stacking context
    var ingredientOverlay = document.querySelector('[data-ingredient-modal-overlay]');
    var documentOverlay = document.querySelector('[data-ingredient-document-overlay]');
    if (ingredientOverlay) document.body.appendChild(ingredientOverlay);
    if (documentOverlay) document.body.appendChild(documentOverlay);

    // State
    let scrollPosition = 0;
    let focusableElements = [];
    let firstFocusable = null;
    let lastFocusable = null;

  // Get modal elements when needed (not on script load)
  function getModalElements() {
    return {
      overlay: document.querySelector('[data-ingredient-modal-overlay]'),
      panel: document.querySelector('[data-ingredient-modal-panel]'),
      closeButton: document.querySelector('[data-ingredient-modal-close]'),
      body: document.querySelector('[data-ingredient-modal-body]'),
      titleSlot: document.querySelector('[data-ingredient-modal-title]'),
      subtitleSlot: document.querySelector('[data-ingredient-modal-subtitle]'),
      imageSlot: document.querySelector('[data-ingredient-modal-image]'),
      whatIsItSlot: document.querySelector('[data-ingredient-modal-what-is-it]'),
      dosageSlot: document.querySelector('[data-ingredient-modal-dosage]'),
      scienceSlot: document.querySelector('[data-ingredient-modal-science]'),
      sourcingSlot: document.querySelector('[data-ingredient-modal-sourcing]'),
      citationsSlot: document.querySelector('[data-ingredient-modal-citations]')
    };
  }

  // Get document viewer elements
  function getDocumentElements() {
    return {
      overlay: document.querySelector('[data-ingredient-document-overlay]'),
      modal: document.querySelector('[data-ingredient-document-modal]'),
      title: document.querySelector('[data-ingredient-document-title]'),
      iframe: document.querySelector('[data-ingredient-document-iframe]'),
      closeButton: document.querySelector('[data-ingredient-document-close]')
    };
  }

  /**
   * Open modal and populate with ingredient data
   */
  function openModal(ingredientId) {
    console.log('openModal called with ID:', ingredientId);
    
    const elements = getModalElements();
    console.log('Modal elements found:', {
      overlay: !!elements.overlay,
      panel: !!elements.panel,
      titleSlot: !!elements.titleSlot
    });
    
    if (!elements.overlay || !elements.panel) {
      console.error('Modal elements not found. Make sure Product Tabs 2 section is loaded.');
      return;
    }

    // Find the template for this ingredient
    const template = document.querySelector(`[data-ingredient-template="${ingredientId}"]`);
    console.log('Template found:', !!template, 'for ID:', ingredientId);
    
    if (!template) {
      console.error('No template found for ingredient:', ingredientId);
      console.log('Available templates:', Array.from(document.querySelectorAll('[data-ingredient-template]')).map(t => t.dataset.ingredientTemplate));
      return;
    }

    // Parse JSON data - MUST use template.content to access template's DocumentFragment
    const jsonScript = template.content.querySelector('script[type="application/json"]');
    if (!jsonScript) {
      console.warn('No JSON data found in template');
      return;
    }
    
    let data;
    try {
      data = JSON.parse(jsonScript.textContent);
    } catch (e) {
      console.error('Failed to parse ingredient data:', e);
      return;
    }

    // Populate header
    elements.titleSlot.textContent = data.title || '';
    if (data.key_function) {
      elements.subtitleSlot.textContent = data.key_function;
      elements.subtitleSlot.style.display = '';
    } else {
      elements.subtitleSlot.style.display = 'none';
    }

    // Populate image
    if (data.image) {
      elements.imageSlot.style.backgroundImage = `url('${data.image}')`;
      elements.imageSlot.style.display = 'block';
      console.log('Image URL set:', data.image);
    } else {
      elements.imageSlot.style.display = 'none';
      console.log('No image data');
    }

    // Clear and populate sections
    clearSlot(elements.whatIsItSlot);
    clearSlot(elements.dosageSlot);
    clearSlot(elements.scienceSlot);
    clearSlot(elements.sourcingSlot);
    clearSlot(elements.citationsSlot);

    if (data.has_description) {
      const descContent = template.content.querySelector('[data-description]');
      if (descContent) {
        elements.whatIsItSlot.appendChild(descContent.cloneNode(true));
      }
    }

    if (data.dosage_value || data.study_dosage_range) {
      const dosageContent = template.content.querySelector('[data-dosage]');
      if (dosageContent) {
        elements.dosageSlot.appendChild(dosageContent.cloneNode(true));
      }
    }

    if (data.has_science) {
      const scienceContent = template.content.querySelector('[data-science]');
      if (scienceContent) {
        elements.scienceSlot.appendChild(scienceContent.cloneNode(true));
      }
    }

    if (data.has_sourcing) {
      const sourcingContent = template.content.querySelector('[data-sourcing]');
      if (sourcingContent) {
        elements.sourcingSlot.appendChild(sourcingContent.cloneNode(true));
      }
    }

    if (data.citations_document) {
      const citationsContent = template.content.querySelector('[data-citations]');
      if (citationsContent) {
        elements.citationsSlot.appendChild(citationsContent.cloneNode(true));
      }
    }

    // Lock scroll WITHOUT position:fixed — that breaks nested touch on iOS Safari
    scrollPosition = window.pageYOffset || document.documentElement.scrollTop;
    document.body.classList.add('ingredient-modal-open');
    document.body.style.overflow = 'hidden';

    // Show modal
    elements.overlay.setAttribute('aria-hidden', 'false');
    
    // Set up focus trap
    setupFocusTrap(elements.panel);
    
    // Focus close button
    setTimeout(() => {
      elements.closeButton.focus();
    }, 50);
  }

  /**
   * Close modal and restore scroll
   */
  function closeModal() {
    const elements = getModalElements();
    
    if (!elements.overlay) return;
    
    elements.overlay.setAttribute('aria-hidden', 'true');
    
    // Unlock scroll
    document.body.classList.remove('ingredient-modal-open');
    document.body.style.overflow = '';
    // No scroll restoration needed — body never moved (no position:fixed)

    // Reset modal body scroll
    if (elements.body) {
      elements.body.scrollTop = 0;
    }
  }

  /**
   * Clear a content slot
   */
  function clearSlot(slot) {
    if (!slot) return;
    while (slot.firstChild) {
      slot.removeChild(slot.firstChild);
    }
  }

  /**
   * Set up focus trap inside modal
   */
  function setupFocusTrap(panel) {
    if (!panel) return;
    
    focusableElements = panel.querySelectorAll(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );
    
    if (focusableElements.length > 0) {
      firstFocusable = focusableElements[0];
      lastFocusable = focusableElements[focusableElements.length - 1];
    }
  }

  /**
   * Handle keyboard navigation (Tab trap and ESC)
   */
  function handleKeyDown(e) {
    const elements = getModalElements();
    
    // ESC to close
    if (e.key === 'Escape' && elements.overlay && elements.overlay.getAttribute('aria-hidden') === 'false') {
      closeModal();
      return;
    }

    // Tab trap
    if (e.key === 'Tab' && elements.overlay && elements.overlay.getAttribute('aria-hidden') === 'false') {
      if (e.shiftKey) {
        // Shift + Tab
        if (document.activeElement === firstFocusable) {
          e.preventDefault();
          lastFocusable.focus();
        }
      } else {
        // Tab
        if (document.activeElement === lastFocusable) {
          e.preventDefault();
          firstFocusable.focus();
        }
      }
    }
  }

  // Event listeners - ALWAYS set up, regardless of modal existence
  
  // Open modal on card click (event delegation)
  document.addEventListener('click', (e) => {
    // Check if click is on the card or any child element
    const card = e.target.closest('[data-open-ingredient-modal]');
    if (card) {
      e.preventDefault();
      e.stopPropagation();
      
      const ingredientId = card.dataset.ingredientId;
      console.log('Ingredient card clicked, ID:', ingredientId);
      
      if (ingredientId) {
        openModal(ingredientId);
      } else {
        console.warn('Card clicked but no ingredient ID found');
      }
    }
  }, true); // Use capture phase to catch events early

  // Close modal on close button click (event delegation)
  document.addEventListener('click', (e) => {
    if (e.target.closest('[data-ingredient-modal-close]')) {
      e.preventDefault();
      closeModal();
    }
  });

  // Close modal on overlay click (but not panel click)
  document.addEventListener('click', (e) => {
    const elements = getModalElements();
    if (elements.overlay && e.target === elements.overlay) {
      e.preventDefault();
      closeModal();
    }
  });

  // Keyboard handling
  document.addEventListener('keydown', handleKeyDown);

  // Prevent body scroll when modal is open (additional safeguard)
  document.addEventListener('wheel', (e) => {
    const elements = getModalElements();
    if (elements.overlay && e.target === elements.overlay) {
      e.preventDefault();
    }
  }, { passive: false });

  /**
   * Open document viewer
   */
  function openDocumentViewer(url, name) {
    const docElements = getDocumentElements();
    if (!docElements.overlay || !docElements.modal) return;

    docElements.title.textContent = name || 'Document';
    docElements.iframe.src = url;
    docElements.overlay.setAttribute('aria-hidden', 'false');
    document.body.classList.add('ingredient-document-open');

    // Focus close button
    setTimeout(() => {
      docElements.closeButton.focus();
    }, 50);
  }

  /**
   * Close document viewer
   */
  function closeDocumentViewer() {
    const docElements = getDocumentElements();
    if (!docElements.overlay) return;

    docElements.overlay.setAttribute('aria-hidden', 'true');
    docElements.iframe.src = '';
    document.body.classList.remove('ingredient-document-open');
  }

  // Open document viewer on citation link click
  document.addEventListener('click', (e) => {
    const link = e.target.closest('[data-open-document]');
    if (link) {
      e.preventDefault();
      const url = link.dataset.documentUrl;
      const name = link.dataset.documentName || 'Document';
      if (url) {
        openDocumentViewer(url, name);
      }
    }
  });

  // Close document viewer
  document.addEventListener('click', (e) => {
    const docElements = getDocumentElements();
    if (e.target.closest('[data-ingredient-document-close]')) {
      e.preventDefault();
      closeDocumentViewer();
    }
    if (docElements.overlay && e.target === docElements.overlay) {
      e.preventDefault();
      closeDocumentViewer();
    }
  });

  // ESC to close document viewer
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') {
      const docElements = getDocumentElements();
      if (docElements.overlay && docElements.overlay.getAttribute('aria-hidden') === 'false') {
        closeDocumentViewer();
      }
    }
  });

  } // end init function

})();
